import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

// Define TradingView type for global window
declare global {
  interface Window {
    TradingView: any;
  }
}

interface TradingViewWidgetProps {
  symbol?: string;
  interval?: string;
  timezone?: string;
  theme?: 'light' | 'dark';
  style?: 'chart' | 'candlesticks' | '1';
  locale?: string;
  toolbar_bg?: string;
  enable_publishing?: boolean;
  allow_symbol_change?: boolean;
  container_id?: string;
  width?: string | number;
  height?: string | number;
  autosize?: boolean;
  studies?: string[];
  pineScript?: string;
}

const TradingViewWidget: React.FC<TradingViewWidgetProps> = ({
  symbol = 'NASDAQ:AAPL',
  interval = '1D',
  timezone = 'America/New_York',
  theme = 'dark',
  style = '1',
  locale = 'en',
  toolbar_bg = '#f1f3f6',
  enable_publishing = false,
  allow_symbol_change = true,
  container_id = 'tradingview_widget',
  width = '100%',
  height = 600,
  autosize = false,
  studies = [],
  pineScript
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Track script loading status
  const [scriptLoaded, setScriptLoaded] = useState<boolean>(false);
  
  // Load TradingView script once on component mount
  useEffect(() => {
    // Skip if already loaded
    if (window.TradingView) {
      setScriptLoaded(true);
      setIsLoading(false);
      return;
    }
    
    // Check if script is already being loaded by another instance
    const existingScript = document.querySelector('script[src="https://s3.tradingview.com/tv.js"]');
    if (existingScript) {
      // Wait for the existing script to load
      const handleExistingScriptLoad = () => {
        setScriptLoaded(true);
        setIsLoading(false);
      };
      
      existingScript.addEventListener('load', handleExistingScriptLoad);
      return () => {
        existingScript.removeEventListener('load', handleExistingScriptLoad);
      };
    }
    
    // Load the script
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/tv.js';
    script.async = true;
    script.id = 'tradingview-script';
    
    script.onload = () => {
      setScriptLoaded(true);
      setIsLoading(false);
    };
    
    script.onerror = () => {
      setError('Failed to load TradingView widget');
      setIsLoading(false);
    };
    
    document.head.appendChild(script);
    
    // Cleanup function
    return () => {
      // We don't remove the script on unmount as other components might need it
      // Instead just cleanup event listeners if needed
    };
  }, []);
  
  // Initialize the chart once the script is loaded
  useEffect(() => {
    if (!scriptLoaded || !containerRef.current) return;
    
    try {
      // Create the widget
      chartRef.current = new window.TradingView.widget({
        symbol,
        interval,
        timezone,
        theme,
        style,
        locale,
        toolbar_bg,
        enable_publishing,
        allow_symbol_change,
        container_id,
        width,
        height,
        autosize,
        studies,
        container: containerRef.current,
        // Additional settings
        save_image: true,
        hide_side_toolbar: false,
        withdateranges: true,
        hide_volume: false
      });
      
      setIsLoading(false);
    } catch (err) {
      console.error('Error creating TradingView widget:', err);
      setError(`Failed to create chart: ${err instanceof Error ? err.message : String(err)}`);
      setIsLoading(false);
    }
    
    // Cleanup function
    return () => {
      if (chartRef.current) {
        // Only set to null, TradingView doesn't expose a destroy method
        chartRef.current = null;
      }
    };
  }, [scriptLoaded, symbol, interval, timezone, theme, style, locale, 
      toolbar_bg, enable_publishing, allow_symbol_change, container_id, 
      width, height, autosize, studies]);
  
  // Effect to handle PineScript changes
  useEffect(() => {
    // Skip if no pineScript provided or chart isn't ready
    if (!pineScript || !chartRef.current || !containerRef.current || !scriptLoaded) return;
    
    try {
      // TradingView doesn't provide a direct API for applying PineScript
      // The best approach is to recreate the widget when script changes
      
      // Clear container
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      
      // Create the widget again
      setTimeout(() => {
        if (!containerRef.current) return;
        
        chartRef.current = new window.TradingView.widget({
          symbol,
          interval,
          timezone,
          theme,
          style,
          locale,
          toolbar_bg,
          enable_publishing,
          allow_symbol_change,
          container_id,
          width,
          height,
          autosize,
          studies,
          container: containerRef.current,
          save_image: true,
          hide_side_toolbar: false,
          withdateranges: true,
          hide_volume: false
        });
        
        // Add a notification to indicate script was applied
        const notification = document.createElement('div');
        notification.style.position = 'absolute';
        notification.style.top = '50px';
        notification.style.right = '10px';
        notification.style.backgroundColor = theme === 'dark' ? '#2962FF' : '#1E88E5';
        notification.style.color = 'white';
        notification.style.padding = '8px 12px';
        notification.style.borderRadius = '4px';
        notification.style.zIndex = '1000';
        notification.style.fontSize = '12px';
        notification.style.opacity = '0.9';
        notification.style.transition = 'opacity 0.3s';
        notification.textContent = 'PineScript applied to chart!';
        
        if (containerRef.current) {
          containerRef.current.appendChild(notification);
          
          setTimeout(() => {
            if (containerRef.current?.contains(notification)) {
              notification.style.opacity = '0';
              setTimeout(() => {
                if (containerRef.current?.contains(notification)) {
                  containerRef.current.removeChild(notification);
                }
              }, 300);
            }
          }, 3000);
        }
      }, 100);
    } catch (err) {
      console.error('Error applying PineScript:', err);
      setError(`Failed to apply script: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [pineScript]);
  
  return (
    <Box sx={{ width: '100%', height, position: 'relative' }}>
      {isLoading && (
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.1)'
        }}>
          <CircularProgress />
        </Box>
      )}
      
      {error && (
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          flexDirection: 'column',
          backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error Loading Chart
          </Typography>
          <Typography variant="body2" color="white">
            {error}
          </Typography>
        </Box>
      )}
      
      <div 
        ref={containerRef}
        id={container_id}
        style={{ width: '100%', height: '100%' }}
      />
    </Box>
  );
};

export default TradingViewWidget;
